import React, { useState, useCallback } from 'react';
import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';

interface CurrencyInputProps extends Omit<InputNumberProps, 'formatter' | 'parser'> {
  /** Currency symbol (e.g., '$', '€', '£') */
  currencySymbol?: string;
  /** Position of currency symbol */
  currencyPosition?: 'prefix' | 'suffix';
  /** Thousand separator (e.g., ',', '.', ' ') */
  thousandSeparator?: string;
  /** Decimal separator (e.g., '.', ',') */
  decimalSeparator?: string;
  /** Number of decimal places */
  decimalPlaces?: number;
  /** Custom formatter function - overrides default currency formatting */
  customFormatter?: (value: string | number | undefined) => string;
  /** Custom parser function - overrides default currency parsing */
  customParser?: (displayValue: string | undefined) => string | number;
  /** Show currency symbol */
  showCurrencySymbol?: boolean;
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  currencySymbol = '$',
  currencyPosition = 'prefix',
  thousandSeparator = ',',
  decimalSeparator = '.',
  decimalPlaces = 2,
  customFormatter,
  customParser,
  showCurrencySymbol = true,
  onBlur,
  onPressEnter,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [rawValue, setRawValue] = useState<string>('');

  // Helper function to clean formatted value for editing
  const cleanValue = useCallback((value: string): string => {
    return value
      .replace(new RegExp(`[${currencySymbol}\\s]`, 'g'), '')
      .replace(new RegExp(`\\${decimalSeparator !== '.' ? decimalSeparator : ','}`, 'g'), '.')
      .replace(new RegExp(`\\${thousandSeparator !== '.' ? thousandSeparator : ','}`, 'g'), '');
  }, [currencySymbol, decimalSeparator, thousandSeparator]);

  // Formatter: only format when not focused
  const formatter = useCallback((value: string | number | undefined): string => {
    if (customFormatter) return customFormatter(value);
    if (isFocused) return rawValue || String(value || '');
    if (!value && value !== 0) return '';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    const isNegative = numValue < 0;
    const absValue = Math.abs(numValue);
    const formattedNumber = absValue.toFixed(decimalPlaces);
    const [integerPart, decimalPart] = formattedNumber.split('.');

    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    const fullNumber = decimalPlaces > 0 && decimalPart
      ? `${formattedInteger}${decimalSeparator}${decimalPart}`
      : formattedInteger;

    const signedNumber = isNegative ? `-${fullNumber}` : fullNumber;

    if (!showCurrencySymbol) return signedNumber;
    return currencyPosition === 'prefix'
      ? `${currencySymbol}${signedNumber}`
      : `${signedNumber}${currencySymbol}`;
  }, [customFormatter, isFocused, rawValue, currencySymbol, currencyPosition, thousandSeparator, decimalSeparator, decimalPlaces, showCurrencySymbol]);

  // Parser: clean the input value
  const parser = useCallback((displayValue: string | undefined): string | number => {
    if (customParser) return customParser(displayValue);
    if (!displayValue) return '';

    if (isFocused) setRawValue(displayValue);

    const cleanedValue = cleanValue(displayValue);
    const parsed = parseFloat(cleanedValue);
    return isNaN(parsed) ? '' : parsed;
  }, [customParser, isFocused, cleanValue]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (e.target.value) setRawValue(cleanValue(e.target.value));
  }, [cleanValue]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setRawValue('');
    onBlur?.(e);
  }, [onBlur]);

  const handlePressEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setRawValue('');
    onPressEnter?.(e);
    setTimeout(() => (e.target as HTMLElement).focus(), 0);
  }, [onPressEnter]);

  return (
    <InputNumber
      {...rest}
      formatter={formatter}
      parser={parser}
      precision={decimalPlaces}
      changeOnWheel={true}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPressEnter={handlePressEnter}
    />
  );
};

export default CurrencyInput;
